/**
 * Vorlage „Form einfetten und bemehlen“.
 *
 * Ein Pinsel streicht Butter auf Boden und Rand der Springform. Dann rieselt
 * Mehl (oder Semmelbrösel) hinein, die Form wird geschwenkt, bis alles
 * gleichmäßig bedeckt ist.
 */
import { ease, segment, wave } from '../geometry.ts';
import { STROKE } from '../palette.ts';
import { path } from '../paths.ts';
import { circle, compact, faded, group, shape, styles } from '../scene.ts';
import type { SceneNode } from '../scene.ts';
import { falling } from '../parts/effects.ts';
import { flourSieve } from '../parts/kitchen.ts';
import { arcArrow, counter, doneMark, GROUND_Y, shadow } from '../parts/stage.ts';
import { brush, place } from '../parts/utensils.ts';
import { cakeTin } from '../parts/vessels.ts';
import { choice, defineTemplate, textValue } from '../template.ts';

const TIN = { cx: 112, width: 124, height: 38 } as const;
const TIN_RY = TIN.width * 0.16;
const RIM_Y = GROUND_Y - TIN.height - TIN_RY;

/** Zeitplan. */
const TIME = {
  brushIn: [0.03, 0.08],
  brush: [0.08, 0.42],
  brushOut: [0.42, 0.47],
  dust: [0.48, 0.64],
  swirl: [0.62, 0.8],
  done: [0.8, 0.84],
  reset: [0.9, 1],
} as const;

/** Beschichtungen. */
const COATS = { flour: 'Mehl', crumbs: 'Semmelbrösel', none: 'nur einfetten' } as const;
type Coat = keyof typeof COATS;

/** Vorlage „Form einfetten und bemehlen“. */
export const bakeGreaseTemplate = defineTemplate({
  id: 'bake.grease',
  group: 'bake',
  title: 'Form einfetten',
  description:
    'Boden und Rand der Backform werden mit dem Pinsel dünn mit Butter bestrichen und danach mit Mehl oder Semmelbröseln ausgestreut.',
  durationSeconds: 7,
  keywords: [
    'einfetten',
    'fetten',
    'ausfetten',
    'bemehlen',
    'mehlen',
    'ausstreuen',
    'springform',
    'backform',
    'form fetten',
    'semmelbrösel',
  ],
  stillTime: 0.3,
  parameters: [choice('coat', 'Danach ausstreuen mit', COATS, 'flour')],
  inferValues(text) {
    const lower = text.toLowerCase();
    if (/brösel|paniermehl/.test(lower)) return { coat: 'crumbs' };
    if (/mehl|bemehlen/.test(lower)) return { coat: 'flour' };
    if (/fetten|butter/.test(lower)) return { coat: 'none' };
    return {};
  },
  scene(values, t) {
    const coat = textValue(values, 'coat', 'flour') as Coat;
    const tin = cakeTin(TIN.cx, RIM_Y, TIN.width, TIN.height);
    const reset = ease.inOutSine(segment(t, ...TIME.reset));
    const greased = segment(t, ...TIME.brush);
    const coated = coat === 'none' ? 0 : ease.inOutSine(segment(t, TIME.dust[0] + 0.04, TIME.swirl[1]));
    const swirl = coat === 'none' ? 0 : Math.sin(segment(t, ...TIME.swirl) * Math.PI * 2) * 9;

    // Innenseite: Butterstriche auf der Rückwand, dann deckende Schicht.
    const strokes: SceneNode[] = [0.15, 0.32, 0.5, 0.68, 0.85]
      .filter((_f, index) => greased > (index + 0.3) / 5)
      .map((f) => {
        const x = TIN.cx - TIN.width / 2 + TIN.width * f;
        return shape(
          path()
            .move(x - 6, RIM_Y - TIN_RY * 0.6 + 3)
            .quad(x, RIM_Y + 2, x + 6, RIM_Y + TIN_RY * 0.5)
            .toString(),
          { stroke: 'paper', strokeWidth: STROKE.outline },
        );
      });
    const coatLayer = (amount: number): SceneNode | null => {
      if (amount <= 0.01) {
        return null;
      }
      const tone = coat === 'crumbs' ? 'shade' : 'snow';
      const specks: SceneNode[] =
        coat === 'crumbs'
          ? [-40, -24, -8, 8, 24, 40, -32, 0, 30, -16, 16].map((dx, index) =>
              circle(TIN.cx + dx, RIM_Y + ((index % 3) - 1) * 6, 1.4, styles.flat('deep')),
            )
          : [];
      return faded(amount, [shape(tin.opening, styles.flat(tone)), ...specks]);
    };
    const inside = (amount: { grease: number; coat: number }): SceneNode =>
      group([...(amount.grease > 0 ? strokes : []), coatLayer(amount.coat)], { clip: tin.opening });

    const brushOn =
      ease.inOutCubic(segment(t, ...TIME.brushIn)) * (1 - ease.inOutCubic(segment(t, ...TIME.brushOut)));
    const brushX = TIN.cx + wave(greased, 2.5, -0.25) * TIN.width * 0.32;
    const sieveOn =
      coat === 'none' ? 0 : Math.sin(segment(t, TIME.dust[0] - 0.02, TIME.dust[1] + 0.02) * Math.PI);
    const done = segment(t, ...TIME.done) * (1 - segment(t, 0.88, 0.9));
    return compact([
      ...counter(),
      shadow(TIN.cx, TIN.width * 0.42),
      group(
        [
          ...tin.back,
          reset > 0 ? group([inside({ grease: 0, coat: 0 })]) : null,
          faded(1 - reset, [inside({ grease: greased, coat: coated })]),
          shape(tin.opening, styles.outline()),
          faded(brushOn, [place(brushX, RIM_Y + 2, -24 + wave(t, 6) * 6, brush(), 1.1)]),
          ...tin.front,
        ],
        { transform: { rotate: swirl, ox: TIN.cx, oy: GROUND_Y } },
      ),
      faded(sieveOn, [
        ...falling(TIN.cx + 2, 46, RIM_Y, 40, 16, t, {
          seed: 3,
          size: 1.3,
          loops: 5,
          tone: coat === 'crumbs' ? 'deep' : 'shade',
        }),
        ...flourSieve(TIN.cx + wave(t, 10) * 4, 30, 56),
      ]),
      faded(ease.outBack(done), [doneMark(208, 36, 1.2)]),
    ]);
  },
  hints() {
    return [arcArrow(TIN.cx, RIM_Y, 50, 13, 200, 340)];
  },
});
